import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { useBatchActionDialog } from './useBatchActionDialog';
import { useBatchProgress } from './useBatchProgress';
import Header from '../Header';
import { useFormatMessage } from '../../../locale';
import { useShowConfirmDialog } from '../FormActions/useShowConfirmDialog';
import { useSelector } from '../../../store';
import usePageContext from '../../creational/PageLoader/usePageContext';
import HTMLPrinter from '../../highLevel/HTMLPrinter';

function BatchActionDialog() {
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  const showConfirmDialog = useShowConfirmDialog();

  const batchActionDialog = useSelector((state) => state.batchActionDialog);
  const { closeBatchActionDialog } = useBatchActionDialog();

  const handleFinish = () => {
    pageContext?.reload();
  };

  const {
    progress,
    error: progressError,
    setProgress,
    getProgressPercentage,
    getProgressText,
    stopPolling,
    clearError,
  } = useBatchProgress(batchActionDialog?.requestConfig || null, handleFinish);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (batchActionDialog && progress?.status === 'pending') {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    if (batchActionDialog && progress?.status === 'pending') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [batchActionDialog, progress]);

  const handleClose = async () => {
    if (
      progress?.status === 'fulfilled' &&
      progress?.failureCount !== null &&
      progress?.failureCount > 0
    ) {
      await showConfirmDialog({
        body: formatMessage('confirmCloseWithErrors'),
      });
    }

    stopPolling();
    setProgress(null);
    clearError();
    closeBatchActionDialog();
  };

  if (!batchActionDialog) return null;

  const progressPercentage = getProgressPercentage();
  const progressText = getProgressText();
  const canClose = progress?.status === 'fulfilled' || !!progressError;

  return (
    <Dialog
      open={!!batchActionDialog}
      onClose={canClose ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={!canClose}
      disableEscapeKeyDown={!canClose}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header heading={formatMessage('processingRecords')} />
          <IconButton onClick={handleClose} disabled={!canClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          {progressError && (
            <Box mb={2}>
              <Alert severity="error">{progressError}</Alert>
            </Box>
          )}

          {!progressError && progress?.status !== 'fulfilled' && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body2" color="textPrimary">
                {progressText}
              </Typography>
            </Box>
          )}

          {!progressError && progress?.status !== 'fulfilled' && (
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              style={{ height: 8, borderRadius: 4 }}
            />
          )}

          {!progressError &&
            progress?.status === 'fulfilled' &&
            progress?.result?.html && (
              <HTMLPrinter html={progress?.result?.html} />
            )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default BatchActionDialog;
